/**
 * Which way this console signs people in: passkeys, or a PIN only.
 *
 * The API says so twice. `GET /auth/mode` is anonymous and answers `{ auth_mode }` before
 * anyone is signed in, and the session bootstrap (`GET /auth/session`) repeats it on the
 * session. A missing field or a 404 on `/auth/mode` means an older API, which is passkeys,
 * what every build before this one did.
 *
 * The login and lock screens render before there is a session to read, so the mode is
 * looked for in this order, and the first answer wins:
 *
 *   1. `auth_mode` on a session the bootstrap returned
 *   2. `auth_mode` on the problem body the bootstrap returned instead (a 401 can carry it)
 *   3. the answer from `GET /auth/mode`
 *   4. passkey, when `/auth/mode` answered 404 (an older API with no such route)
 *
 * Nothing is guessed. If `/auth/mode` cannot be reached at all (network down, a 5xx) and
 * nothing else has said, the screens draw a neutral "can't reach the sign-in service" state
 * with a Retry button. No remembered value, no build setting: a wrong guess draws the wrong
 * screen, and a wrong screen on a sign-in page is worse than a wait. While the call is in
 * flight the screens draw a neutral wait, never a passkey button.
 *
 * Nothing here is a control. In PIN mode the passkey routes answer 404 on the server, and
 * in passkey mode a PIN login without an enrolled device is refused there too. This only
 * decides which buttons to draw.
 */
import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Session } from '@elutide/shared';
import { ApiProblem, api } from '@/lib/api';
import { qk } from '@/lib/keys';

export type AuthMode = 'pin' | 'passkey';

/** The session as this build reads it. `auth_mode` lands in `@elutide/shared` with the API. */
export type SessionWithMode = Session & { auth_mode?: AuthMode };

/** What `GET /auth/mode` answers. */
export interface AuthModeAnswer {
  auth_mode: AuthMode;
}

/** The one sentence the screens show when the mode cannot be learned. */
export const AUTH_MODE_UNREACHABLE_MESSAGE = "Can't reach the sign-in service.";

export function authModeFrom(payload: unknown): AuthMode | null {
  if (!payload || typeof payload !== 'object') return null;
  const value = (payload as { auth_mode?: unknown }).auth_mode;
  return value === 'pin' || value === 'passkey' ? value : null;
}

export interface ResolveAuthModeInput {
  /** What `GET /auth/session` returned, if it succeeded. */
  session?: unknown;
  /** What `GET /auth/session` threw, if it failed. */
  error?: unknown;
  /** What `GET /auth/mode` returned, if it succeeded. */
  announced?: unknown;
  /** What `GET /auth/mode` threw, if it failed. */
  announcedError?: unknown;
}

/**
 * The mode, or null when nobody has said. Null is not passkey: a caller that draws a
 * passkey button on null would flash the wrong screen on a flaky network.
 */
export function resolveAuthMode(input: ResolveAuthModeInput): AuthMode | null {
  const fromSession = authModeFrom(input.session);
  if (fromSession) return fromSession;
  if (input.error instanceof ApiProblem) {
    const fromProblem = authModeFrom(input.error.problem);
    if (fromProblem) return fromProblem;
  }
  const fromAnnounced = authModeFrom(input.announced);
  if (fromAnnounced) return fromAnnounced;
  // The route exists on every API that knows about PIN mode. A 404 is an older API, and an
  // older API is passkeys. Any other failure is unknown, and stays unknown.
  if (input.announcedError instanceof ApiProblem && input.announcedError.status === 404) {
    return 'passkey';
  }
  return null;
}

export interface AuthModeState {
  /**
   * The mode once known. Until `resolved` is true this is passkey only as a type default;
   * a screen must not draw a passkey button on it.
   */
  mode: AuthMode;
  /** True once one of the sources above has answered. */
  resolved: boolean;
  /**
   * True when `/auth/mode` failed for a reason other than 404 and nothing else has said.
   * The screens draw the unreachable state and offer `retry`.
   */
  unreachable: boolean;
  /** Asks `/auth/mode` again. */
  retry: () => void;
}

/**
 * Asks `/auth/mode` once (cached five minutes, one retry) and shares the session query the
 * shell already runs. On a login or lock screen the session query fails, which is expected,
 * and the order above applies.
 */
export function useAuthMode(): AuthModeState {
  const announced = useQuery({
    queryKey: qk.authMode(),
    queryFn: () => api.get<AuthModeAnswer>('/auth/mode'),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const session = useQuery({
    queryKey: qk.session(),
    queryFn: () => api.get<SessionWithMode>('/auth/session'),
    staleTime: 60_000,
    retry: false,
  });

  const refetchAnnounced = announced.refetch;
  const retry = useCallback(() => {
    void refetchAnnounced();
  }, [refetchAnnounced]);

  const mode = resolveAuthMode({
    session: session.data,
    error: session.error,
    announced: announced.data,
    announcedError: announced.error,
  });
  return {
    mode: mode ?? 'passkey',
    resolved: mode !== null && !announced.isPending && !session.isPending,
    // A retry in flight is a wait again, not the failure it is retrying.
    unreachable: mode === null && !session.isPending && announced.isError && !announced.isFetching,
    retry,
  };
}
